import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Joyride, { CallBackProps, STATUS, Step } from 'react-joyride';
import { EventStatus, OwnerEvent, clubsBookingApi } from '@/api/clubs-booking';
import {
  AdminDataTable,
  AdminSection,
  adminInputClassName,
  adminPrimaryButtonClassName,
  adminTableCellClassName,
  adminTableClassName,
  adminTableHeadCellClassName,
  adminToolbarActionsClassName,
  adminToolbarClassName,
} from '@/components/admin/AdminLayout';
import AdminMobileRowActions from '@/components/admin/AdminMobileRowActions';
import AdminShell from '@/components/admin/AdminShell';
import ConfirmModal from '@/components/common/ConfirmModal';
import TablePagination from '@/components/common/TablePagination';

const activitiesEmptyTourSteps: Step[] = [
  {
    target: '[data-tour-activities-header]',
    placement: 'bottom',
    disableBeacon: true,
    content: 'This page is where you manage your club activities.',
  },
  {
    target: '[data-tour-activities-filter]',
    placement: 'bottom',
    disableBeacon: true,
    content: 'Use this filter to quickly switch between live, draft, and cancelled activities.',
  },
  {
    target: '[data-tour-activities-create]',
    placement: 'left',
    disableBeacon: true,
    content: 'Start here to create your first activity and open bookings for parents.',
  },
];

const ActivitiesPage = () => {
  const [events, setEvents] = useState<OwnerEvent[]>([]);
  const [eventsTotal, setEventsTotal] = useState(0);
  const [eventFilter, setEventFilter] = useState<EventStatus | 'all'>('all');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [deleteTarget, setDeleteTarget] = useState<OwnerEvent | null>(null);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(7);
  const [dismissedEmptyTour, setDismissedEmptyTour] = useState(false);

  const loadEvents = useCallback(async () => {
    setLoading(true);
    try {
      const data = await clubsBookingApi.listMyEvents(
        eventFilter === 'all' ? undefined : eventFilter,
        { page, limit: pageSize },
      );
      setEvents(data.items || []);
      setEventsTotal(Number(data.total || 0));
    } catch (err: any) {
      const messageText = err?.response?.data?.message;
      setError(Array.isArray(messageText) ? messageText.join(', ') : messageText || 'Failed to load activities');
    } finally {
      setLoading(false);
    }
  }, [eventFilter, page, pageSize]);

  useEffect(() => {
    void loadEvents();
  }, [loadEvents]);

  useEffect(() => {
    setPage(1);
  }, [eventFilter]);

  useEffect(() => {
    if (events.length > 0) {
      setDismissedEmptyTour(false);
    }
  }, [events.length]);

  const onTourCallback = (data: CallBackProps) => {
    if (data.status === STATUS.FINISHED || data.status === STATUS.SKIPPED) {
      setDismissedEmptyTour(true);
    }
  };

  const shouldRunEmptyTour = !loading && eventsTotal === 0 && !dismissedEmptyTour;

  const toggleEventStatus = async (event: OwnerEvent) => {
    const nextStatus: EventStatus = event.status === 'published' ? 'draft' : 'published';
    await clubsBookingApi.updateEvent(event._id, { status: nextStatus });
    await loadEvents();
  };

  const deleteEvent = async (eventId: string) => {
    await clubsBookingApi.deleteEvent(eventId);
    await loadEvents();
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleteBusy(true);
    try {
      await deleteEvent(deleteTarget._id);
      setDeleteTarget(null);
    } finally {
      setDeleteBusy(false);
    }
  };

  return (
    <AdminShell title="Activities" subtitle="Create, publish, and share bookable activities">
      <Joyride
        run={shouldRunEmptyTour}
        steps={activitiesEmptyTourSteps}
        continuous
        showSkipButton
        disableScrolling
        callback={onTourCallback}
        styles={{
          options: {
            primaryColor: '#1F8F5F',
            zIndex: 10000,
          },
        }}
      />

      {error && <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>}

      <AdminSection>
        <div className={adminToolbarClassName}>
          <h2 className="text-2xl font-semibold" data-tour-activities-header>Activity list</h2>
          <div className={adminToolbarActionsClassName}>
            <select
              className={adminInputClassName}
              value={eventFilter}
              onChange={(e) => setEventFilter(e.target.value as EventStatus | 'all')}
              data-tour-activities-filter
            >
              <option value="all">All statuses</option>
              <option value="published">Published</option>
              <option value="draft">Draft</option>
              <option value="cancelled">Cancelled</option>
            </select>
            <Link
              to="/activities/new"
              className={adminPrimaryButtonClassName}
              data-tour-activities-create
            >
              Create
            </Link>
          </div>
        </div>

        <AdminDataTable>
          <table className={`${adminTableClassName} min-w-[760px] sm:min-w-[840px]`}>
            <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
              <tr>
                <th className={adminTableHeadCellClassName}>Title</th>
                <th className={adminTableHeadCellClassName}>Venue</th>
                <th className={adminTableHeadCellClassName}>Sessions</th>
                <th className={adminTableHeadCellClassName}>Booking link</th>
                <th className={adminTableHeadCellClassName}>Status</th>
                <th className={`${adminTableHeadCellClassName} hidden sm:table-cell`}>Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {events.map((event) => {
                const bookingLink = `${window.location.origin}/book/${event._id}`;
                const canShareBookingLink = event.status === 'published';
                const sessionCount = Array.isArray(event.sessions) ? event.sessions.length : 0;
                return (
                  <tr key={event._id}>
                    <td className={`${adminTableCellClassName} font-medium`}>
                      <div className="flex items-start justify-between gap-3">
                        <Link to={`/activities/${event._id}`} className="min-w-0 text-[#1F8F5F] hover:underline">{event.title}</Link>
                        <AdminMobileRowActions
                          title={event.title}
                          description="Manage this activity."
                          triggerTestId={`activities-row-actions-${event._id}`}
                          items={[
                            { label: 'Overview', to: `/activities/${event._id}` },
                            { label: 'Edit', to: `/activities/${event._id}/edit` },
                            {
                              label: event.status === 'published' ? 'Mark not live' : 'Mark live',
                              onSelect: () => {
                                void toggleEventStatus(event);
                              },
                            },
                            {
                              label: 'Delete',
                              tone: 'danger',
                              onSelect: () => setDeleteTarget(event),
                            },
                          ]}
                        />
                      </div>
                    </td>
                    <td className={adminTableCellClassName}>
                      <div>{event.venueName || event.location}</div>
                      <div className="text-xs text-slate-500">
                        {event.venueCity && event.venuePostcode ? `${event.venueCity}, ${event.venuePostcode}` : ''}
                      </div>
                    </td>
                    <td className={adminTableCellClassName}>{sessionCount}</td>
                    <td className={`${adminTableCellClassName} text-[#1F8F5F]`}>
                      {canShareBookingLink ? (
                        <button onClick={() => void navigator.clipboard.writeText(bookingLink)} className="hover:underline">Copy link</button>
                      ) : (
                        <span className="text-xs text-slate-500">Mark live to get link</span>
                      )}
                    </td>
                    <td className={`${adminTableCellClassName} capitalize`}>{event.status === 'published' ? 'Live' : 'Not live'}</td>
                    <td className={`${adminTableCellClassName} hidden sm:table-cell`}>
                      <div className="flex flex-wrap gap-2">
                        <Link to={`/activities/${event._id}`} className="rounded-md border border-slate-300 px-3 py-1 text-xs font-medium hover:bg-slate-50">Overview</Link>
                        <Link to={`/activities/${event._id}/edit`} className="rounded-md border border-slate-300 px-3 py-1 text-xs font-medium hover:bg-slate-50">Edit</Link>
                        <button className="rounded-md border border-slate-300 px-3 py-1 text-xs font-medium hover:bg-slate-50" onClick={() => void toggleEventStatus(event)}>
                          {event.status === 'published' ? 'Mark not live' : 'Mark live'}
                        </button>
                        <button className="rounded-md border border-red-200 px-3 py-1 text-xs font-medium text-red-700 hover:bg-red-50" onClick={() => setDeleteTarget(event)}>Delete</button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          {loading && <p className="px-4 py-6 text-sm text-slate-500">Loading activities...</p>}
          {!loading && eventsTotal === 0 && (
            <p className="px-4 py-6 text-sm text-slate-500" data-tour-activities-empty>
              No activities found.
            </p>
          )}
          {!loading && eventsTotal > 0 && (
            <TablePagination
              totalItems={eventsTotal}
              currentPage={page}
              pageSize={pageSize}
              onPageChange={setPage}
              onPageSizeChange={(size) => {
                setPageSize(size);
                setPage(1);
              }}
            />
          )}
        </AdminDataTable>
      </AdminSection>

      <ConfirmModal
        open={!!deleteTarget}
        title="Delete activity"
        message={`Delete "${deleteTarget?.title || 'this activity'}"? This action cannot be undone.`}
        confirmLabel="Delete activity"
        busy={deleteBusy}
        onCancel={() => setDeleteTarget(null)}
        onConfirm={() => void confirmDelete()}
      />
    </AdminShell>
  );
};

export default ActivitiesPage;
